import {lazy} from 'react';
import RoleNames from './lib/RoleNames';
import {RoutePaths} from './RoutePaths';
import {Authorization} from './types';

const HomePage = lazy(() => import('./components/pages/HomePage'));
const LoginPage = lazy(() => import('./components/accounts/LoginPage'));
const UserRegistrationPage = lazy(
    () => import('./components/accounts/UserRegistrationPage')
);
const PasswordlessLoginPage = lazy(
    () => import('./components/accounts/PasswordlessLoginPage')
);
const ProfilePage = lazy(() => import('./components/accounts/ProfilePage'));
const DebugPage = lazy(() => import('./components/sysadmin/DebugPage'));
const UsersPage = lazy(
    () => import('./components/sysadmin/users/UsersPage.tsx')
);
const UserEditPage = lazy(
    () => import('./components/sysadmin/users/UserEditPage.tsx')
);
const UserRegistrationSocialPage = lazy(
    () => import('./components/social_logins/UserRegistrationSocialPage')
);
const EmailConfirmationPage = lazy(
    () => import('./components/accounts/EmailConfirmationPage')
);
const ChangePasswordPage = lazy(
    () => import('./components/accounts/ChangePasswordPage')
);
const ForgotPasswordPage = lazy(
    () => import('./components/accounts/ForgotPasswordPage')
);
const ResetPasswordPage = lazy(
    () => import('./components/accounts/ResetPasswordPage')
);
const PrivacyPage = lazy(() => import('./components/pages/PrivacyPage'));
const TermsAndConditionsPage = lazy(
    () => import('./components/pages/TermsAndConditionsPage')
);
const FeaturesPage = lazy(
    () => import('./components/pages/marketing/FeaturesPage')
);
const WhyCertavuePage = lazy(
    () => import('./components/pages/marketing/WhyCertavuePage')
);
const PricingPage = lazy(
    () => import('./components/pages/marketing/PricingPage')
);
const AboutUsPage = lazy(
    () => import('./components/pages/marketing/AboutUsPage')
);
const ProductTourPage = lazy(
    () => import('./components/product-tour/ProductTourPage')
);
const ThemePage = lazy(() => import('./components/sysadmin/ThemePage'));
const CompanyRegistrationPage = lazy(
    () => import('./components/customers/CompanyRegistrationPage')
);
const GettingStartedPage = lazy(
    () => import('./components/main/getting-started/GettingStartedPage')
);
const DashboardPage = lazy(
    () => import('./components/main/dashboard/DashboardPage.tsx')
);
const ProjectsPage = lazy(
    () => import('./components/main/projects/ProjectsPage.tsx')
);
const ProjectPage = lazy(
    () => import('./components/main/projects/ProjectPage')
);
// Timeline is handled by DashboardPage (ResourceAllocationManager)
const TeamsPage = lazy(() => import('./components/main/teams/TeamsPage'));
const TeamEditPage = lazy(() => import('./components/main/teams/TeamEditPage'));
const ReportsPage = lazy(() => import('./components/main/ReportsPage'));
const ReportingView = lazy(
    () => import('./components/main/reports/ReportingView')
);
const IndividualLoadView = lazy(
    () => import('./components/main/reports/insights/IndividualLoadView')
);
const TeamsLoadView = lazy(
    () => import('./components/main/reports/insights/TeamsLoadView')
);
const MyTeamsRedirect = lazy(() =>
    import('./components/main/reports/insights/TeamsLoadView').then(m => ({
        default: m.MyTeamsRedirect
    }))
);
const OverloadedTeamsRedirect = lazy(() =>
    import('./components/main/reports/insights/TeamsLoadView').then(m => ({
        default: m.OverloadedTeamsRedirect
    }))
);
const ProjectsLoadView = lazy(
    () => import('./components/main/reports/insights/ProjectsLoadView')
);
const MyProjectsRedirect = lazy(() =>
    import('./components/main/reports/insights/ProjectsLoadView').then(m => ({
        default: m.MyProjectsRedirect
    }))
);
const ProjectsAtRiskRedirect = lazy(() =>
    import('./components/main/reports/insights/ProjectsLoadView').then(m => ({
        default: m.ProjectsAtRiskRedirect
    }))
);
const RolePressureView = lazy(
    () => import('./components/main/reports/insights/RolePressureView')
);
const RoleCapacityView = lazy(
    () => import('./components/main/reports/insights/RoleCapacityView')
);
const SustainedOverloadView = lazy(
    () => import('./components/main/reports/insights/SustainedOverloadView')
);
const RebalancingView = lazy(
    () => import('./components/main/reports/insights/RebalancingView')
);
const CrunchTimelineView = lazy(
    () => import('./components/main/reports/insights/CrunchTimelineView')
);
const DemandByInitiativeView = lazy(
    () => import('./components/main/reports/insights/DemandByInitiativeView')
);
const MissingResourcesView = lazy(
    () => import('./components/main/reports/insights/MissingResourcesView')
);
const OpenRoleCandidatesView = lazy(
    () => import('./components/main/reports/insights/OpenRoleCandidatesView')
);
const RoleAvailabilityView = lazy(
    () => import('./components/main/reports/insights/RoleAvailabilityView')
);
const ReceivingLoadView = lazy(
    () => import('./components/main/reports/insights/ReceivingLoadView')
);
const BoardSummaryView = lazy(
    () => import('./components/main/reports/insights/BoardSummaryView')
);
const PresentationPackPage = lazy(
    () => import('./components/main/reports/insights/presentationpack/PresentationPackPage')
);
const UnderUtilisedView = lazy(
    () => import('./components/main/reports/insights/UnderUtilisedView')
);
const ChangeImpactView = lazy(
    () => import('./components/main/reports/insights/ChangeImpactView')
);
const ProgrammeImpactView = lazy(
    () => import('./components/main/reports/insights/ProgrammeImpactView')
);
const AdoptionImpactView = lazy(
    () => import('./components/main/reports/insights/AdoptionImpactView')
);
const PersonManagementPage = lazy(
    () => import('./components/main/people/PersonManagementPage.tsx')
);
const PersonDetailPage = lazy(
    () => import('./components/main/people/PersonDetailPage.tsx')
);
const RolesPage = lazy(() => import('./components/main/people/RolesPage.tsx'));
const RoleDetailPage = lazy(
    () => import('./components/main/people/RoleDetailPage.tsx')
);
const DocumentPage = lazy(() => import('./components/documents/DocumentPage'));
const EditDocumentsPage = lazy(
    () => import('./components/documents/EditDocumentsPage')
);
const DocumentSysAdminPage = lazy(
    () => import('./components/documents/DocumentSysAdminPage')
);
const EditDocumentPage = lazy(
    () => import('./components/documents/EditDocumentPage')
);
const ContentManagementPage = lazy(
    () => import('./components/sysadmin/contents/ContentManagementPage')
);
const ContentFileListPage = lazy(
    () => import('./components/sysadmin/contents/ContentFileListPage')
);
const ContentFileUploadPage = lazy(
    () => import('./components/sysadmin/contents/ContentFileUploadPage')
);
const ContentFilePreviewPage = lazy(
    () => import('./components/sysadmin/contents/ContentFilePreviewPage')
);
const AssistantsPage = lazy(
    () => import('./components/sysadmin/ai/AssistantsPage')
);
const SkillsPage = lazy(() => import('./components/sysadmin/ai/SkillsPage'));
const AiUsagePage = lazy(() => import('./components/sysadmin/ai/AiUsagePage'));
const AiPlatformSettingsPage = lazy(
    () => import('./components/sysadmin/ai/AiPlatformSettingsPage')
);
const AiConnectionsPage = lazy(
    () => import('./components/sysadmin/ai/AiConnectionsPage')
);
const AiConnectionEditPage = lazy(
    () => import('./components/sysadmin/ai/AiConnectionEditPage')
);
const AiProviderModelEditPage = lazy(
    () => import('./components/sysadmin/ai/AiProviderModelEditPage')
);
const SettingsLayout = lazy(
    () => import('./components/main/settings/SettingsLayout')
);
const GeneralSettingsPage = lazy(
    () => import('./components/main/settings/GeneralSettingsPage')
);
const BrandingSettingsPage = lazy(
    () => import('./components/main/settings/BrandingSettingsPage')
);
const AccessControlPage = lazy(
    () => import('./components/main/settings/AccessControlPage')
);
const BillingPage = lazy(
    () => import('./components/main/settings/BillingPage')
);
const McpServersTab = lazy(
    () => import('./components/main/ai-settings/McpServersTab')
);
const ServiceConnectionsTab = lazy(
    () => import('./components/main/ai-settings/ServiceConnectionsTab')
);
const McpTechnicalInfoTab = lazy(
    () => import('./components/main/ai-settings/McpTechnicalInfoTab')
);
const ImportsPage = lazy(
    () => import('./components/main/integrations/IntegrationsPage')
);
const SourcesPage = lazy(
    () => import('./components/main/integrations/SourcesPage')
);
const ClientsPage = lazy(
    () => import('./components/main/integrations/ClientsPage')
);
const IntegrationsOverviewPage = lazy(
    () => import('./components/main/integrations/IntegrationsOverviewPage')
);
const IntegrationClientDetailPage = lazy(
    () => import('./components/main/integrations/IntegrationClientDetailPage')
);
const DirectorySyncPage = lazy(
    () => import('./components/main/directory/DirectorySyncPage')
);
const DirectoryGroupsPage = lazy(
    () => import('./components/main/directory/DirectoryGroupsPage')
);
const DirectorySyncRunPage = lazy(
    () => import('./components/main/directory/DirectorySyncRunPage')
);
const WorkingHoursSettingsPage = lazy(
    () => import('./components/main/settings/capacity/WorkingHoursSettingsPage')
);
const RagThresholdsSettingsPage = lazy(
    () =>
        import('./components/main/settings/capacity/RagThresholdsSettingsPage')
);
const UtilisationSettingsPage = lazy(
    () => import('./components/main/settings/capacity/UtilisationSettingsPage')
);
const NonWorkingDaysSettingsPage = lazy(
    () =>
        import('./components/main/settings/capacity/NonWorkingDaysSettingsPage')
);
const AiUsageSettingsPage = lazy(
    () => import('./components/main/ai-settings/AiUsageSettingsPage')
);
const AssistantsSettingsPage = lazy(
    () => import('./components/main/ai-settings/AssistantsSettingsPage')
);
const ProviderKeysSettingsPage = lazy(
    () => import('./components/main/ai-settings/ProviderKeysSettingsPage')
);
const McpAccessSettingsPage = lazy(
    () => import('./components/main/ai-settings/McpAccessSettingsPage')
);
const McpClientsSettingsPage = lazy(
    () => import('./components/main/ai-settings/McpClientsSettingsPage')
);
const McpGrantsSettingsPage = lazy(
    () => import('./components/main/ai-settings/McpGrantsSettingsPage')
);
const SysAdminLayout = lazy(
    () => import('./components/sysadmin/SysAdminLayout')
);
const ImportWizardPage = lazy(
    () => import('./components/main/integrations/ImportWizardPage')
);
const ImportRunPage = lazy(
    () => import('./components/main/integrations/ImportRunPage')
);
const ImportPackagePage = lazy(
    () => import('./components/main/integrations/ImportPackagePage')
);
const ActivityPage = lazy(
    () => import('./components/main/settings/ActivityPage')
);
const McpConsentPage = lazy(() => import('./components/mcp/McpConsentPage'));
const AssistantEditPage = lazy(
    () => import('./components/sysadmin/ai/AssistantEditPage')
);
const AssistantTestPage = lazy(
    () => import('./components/sysadmin/ai/AssistantTestPage')
);
const TagManagementPage = lazy(
    () => import('./components/main/tags/TagManagement')
);
const DataManagementPage = lazy(
    () => import('./components/sysadmin/DataManagementPage')
);
const CustomersPage = lazy(
    () => import('./components/sysadmin/customers/CustomersPage')
);
const SecretKeysPage = lazy(
    () => import('./components/sysadmin/security/SecretKeysPage')
);
const BillingSettingsPage = lazy(
    () => import('./components/sysadmin/billing/BillingSettingsPage')
);
const PaddleWebhookLogPage = lazy(
    () => import('./components/sysadmin/paddle/PaddleWebhookLogPage')
);
const CustomerEditPage = lazy(
    () => import('./components/sysadmin/customers/CustomerEditPage')
);
// Notifications MVP
const NotificationsPage = lazy(
    () => import('./components/notifications/NotificationsPage')
);
const NotificationSettingsPage = lazy(
    () => import('./components/notifications/NotificationSettingsPage')
);
const DemoEmailPage = lazy(
    () => import('./components/notifications/DemoEmailPage')
);

export type DefaultPageProps = Record<string, never>;

export type Page<T = DefaultPageProps> = Authorization & {
    path: string;
    // Absent only for redirect entries (see redirectTo).
    component?: React.LazyExoticComponent<React.ComponentType<T>>;
    // A moved route: render a redirect to this path instead of a component.
    // ":param" tokens are filled from the matched route (RouteRedirect).
    redirectTo?: string;
    title?: string;
    wrapper?: React.LazyExoticComponent<
        React.ComponentType<{children?: React.ReactNode}>
    >;
    children?: Page<T>[];
    fluidContainer?: boolean;
    // Public/marketing pages: render without the routed Container (and without
    // contentPanel padding) so sections can run full-bleed edge to edge.
    fullBleed?: boolean;
};

// Children of the Settings shell: administrator-only unless given roles (the
// directory pages maintainers manage), fullBleed (the shell owns the padding),
// addressed relative to /settings. Keep roles in step with settingsNav.ts.
const settingsChild = (
    path: string,
    component: Page['component'],
    roles: string[] = [RoleNames.Administrator]
): Page => ({
    path,
    component,
    authorize: true,
    roles,
    fullBleed: true
});

const settingsRedirect = (path: string, to: string): Page => ({
    path,
    redirectTo: to,
    authorize: true,
    roles: [RoleNames.Administrator],
    fullBleed: true
});

// Children of the sysadmin console shell.
const sysadminChild = (path: string, component: Page['component']): Page => ({
    path,
    component,
    authorize: true,
    roles: [RoleNames.SysAdmin],
    fullBleed: true
});

// PARITY: a new app page has three companions.
// - RoutePaths.ts holds its path (never a string literal here); see the PARITY
//   note there for the server mirror and the assistant's navigate_to vocabulary.
// - The page component publishes its context for the assistant with
//   useAssistantPageContext (title, records with ids, period, lens, filters)
//   so "this project" or "these weeks" mean something; the layout already
//   publishes the route, so a page without it is known only by its URL.
// - Settings pages also join settingsNav.ts (sidebar) and use settingsChild.
const pages: Page[] = [
    {
        path: RoutePaths.home(),
        component: HomePage,
        fullBleed: true
    },
    {
        path: RoutePaths.company.register(),
        component: CompanyRegistrationPage
    },
    {
        // Registration used to end on a static page; it now opens Getting started.
        path: RoutePaths.company.completed(),
        redirectTo: RoutePaths.gettingStarted({welcome: true}),
        authorize: true
    },
    {
        path: RoutePaths.gettingStarted(),
        component: GettingStartedPage,
        authorize: true,
        roles: [RoleNames.CustomerUser]
    },
    {
        path: RoutePaths.accounts.login(),
        component: LoginPage,
        fullBleed: true
    },
    {
        path: RoutePaths.accounts.signUp(),
        component: UserRegistrationPage,
        fullBleed: true
    },
    {
        path: RoutePaths.accounts.passwordlessLogin(),
        component: PasswordlessLoginPage,
        fullBleed: true
    },
    {
        path: RoutePaths.accounts.profile(),
        component: ProfilePage,
        authorize: true
    },
    {
        // OAuth consent for the MCP server; handles its own sign-in redirect
        // so the ?session=... query string survives the login round trip.
        path: RoutePaths.mcpConsent(),
        component: McpConsentPage
    },
    {
        path: RoutePaths.accounts.userRegistrationSocial(),
        component: UserRegistrationSocialPage,
        fullBleed: true
    },
    {
        path: RoutePaths.accounts.emailConfirmation(),
        component: EmailConfirmationPage,
        fullBleed: true
    },
    {
        path: RoutePaths.accounts.changePassword(),
        component: ChangePasswordPage,
        fullBleed: true
    },
    {
        path: RoutePaths.accounts.forgotPassword(),
        component: ForgotPasswordPage,
        fullBleed: true
    },
    {
        path: RoutePaths.accounts.resetPassword(),
        component: ResetPasswordPage,
        fullBleed: true
    },
    {
        path: RoutePaths.privacy(),
        component: PrivacyPage,
        fullBleed: true
    },
    {
        path: RoutePaths.termsAndConditions(),
        component: TermsAndConditionsPage,
        fullBleed: true
    },
    {
        path: RoutePaths.features(),
        component: FeaturesPage,
        fullBleed: true
    },
    {
        path: RoutePaths.productTour(),
        component: ProductTourPage,
        fullBleed: true
    },
    {
        path: RoutePaths.whyCertavue(),
        component: WhyCertavuePage,
        fullBleed: true
    },
    {
        path: RoutePaths.pricing(),
        component: PricingPage,
        fullBleed: true
    },
    {
        path: RoutePaths.aboutUs(),
        component: AboutUsPage,
        fullBleed: true
    },
    {
        path: RoutePaths.dashboard(),
        component: DashboardPage,
        authorize: true,
        roles: [RoleNames.CustomerUser],
        fluidContainer: true
    },
    {
        path: RoutePaths.projects.root(),
        component: ProjectsPage,
        authorize: true,
        roles: [RoleNames.CustomerUser],
        fluidContainer: true
    },
    {
        path: RoutePaths.projects.detail(':projectId'),
        component: ProjectPage,
        authorize: true,
        roles: [RoleNames.CustomerUser],
        fluidContainer: true
    },
    {
        path: RoutePaths.teams.root(),
        component: TeamsPage,
        authorize: true,
        roles: [RoleNames.CustomerUser]
    },
    {
        path: RoutePaths.teams.detail(':id'),
        component: TeamEditPage,
        authorize: true,
        roles: [RoleNames.CustomerUser]
    },
    {
        path: RoutePaths.insights.root(),
        component: ReportsPage,
        authorize: true,
        roles: [RoleNames.CustomerUser],
        children: [
            {
                path: 'explore',
                component: ReportingView,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            {
                path: 'individual-load',
                component: IndividualLoadView,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            // Old name for the individual-load insight; kept so bookmarks and
            // stale links keep working.
            {
                path: 'my-load',
                component: IndividualLoadView,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            {
                path: 'teams',
                component: TeamsLoadView,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            {
                path: 'projects',
                component: ProjectsLoadView,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            // Old names for the merged team/project load insights; kept so
            // bookmarks and stale links land on the right selection.
            {
                path: 'my-teams',
                component: MyTeamsRedirect,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            {
                path: 'teams-overloaded',
                component: OverloadedTeamsRedirect,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            {
                path: 'my-projects',
                component: MyProjectsRedirect,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            {
                path: 'projects-at-risk',
                component: ProjectsAtRiskRedirect,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            {
                path: 'missing-resources',
                component: MissingResourcesView,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            {
                path: 'open-role-candidates',
                component: OpenRoleCandidatesView,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            {
                path: 'role-availability',
                component: RoleAvailabilityView,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            {
                path: 'role-pressure',
                component: RolePressureView,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            // Old name for the role-pressure insight; kept so bookmarks and
            // stale links keep working (the querystring travels).
            {
                path: 'role-demand',
                redirectTo: RoutePaths.insights.rolePressure(),
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            {
                path: 'role-capacity',
                component: RoleCapacityView,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            {
                path: 'sustained-overload',
                component: SustainedOverloadView,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            {
                path: 'rebalancing',
                component: RebalancingView,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            {
                path: 'crunch-timeline',
                component: CrunchTimelineView,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            {
                path: 'demand-by-initiative',
                component: DemandByInitiativeView,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            {
                path: 'board',
                component: BoardSummaryView,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            {
                path: 'presentation-pack',
                component: PresentationPackPage,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            {
                path: 'under-utilised',
                component: UnderUtilisedView,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            {
                path: 'change-impact',
                component: ChangeImpactView,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            {
                path: 'receiving-load',
                component: ReceivingLoadView,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            {
                path: 'adoption',
                component: AdoptionImpactView,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            },
            {
                path: 'programme-impact',
                component: ProgrammeImpactView,
                authorize: true,
                roles: [RoleNames.CustomerUser]
            }
        ]
    },
    {
        path: RoutePaths.people.root(),
        component: PersonManagementPage,
        authorize: true,
        roles: RoleNames.DirectoryEditors
    },
    {
        path: RoutePaths.people.detail(':id'),
        component: PersonDetailPage,
        authorize: true,
        roles: RoleNames.DirectoryEditors
    },
    {
        path: RoutePaths.notifications.root(),
        component: NotificationsPage,
        authorize: true
    },
    {
        path: RoutePaths.notifications.settings(),
        component: NotificationSettingsPage,
        authorize: true
    },
    {
        path: RoutePaths.notifications.demoEmail(),
        component: DemoEmailPage,
        authorize: true
    },
    {
        path: RoutePaths.documents.view('*'),
        component: DocumentPage, // docGroup = "docs"
        // Public: the assistant panel appears only for a signed-in customer user (AssistantPanelContext).
        fluidContainer: true
    },
    {
        path: RoutePaths.sysdocs.view('*'),
        component: DocumentSysAdminPage, // docGroup = "sys"
        authorize: true,
        roles: [RoleNames.SysAdmin],
        fluidContainer: true
    },
    // ---------------------------------------------------------------- Settings
    // Customer administration: a full-width shell (SettingsLayout) with a
    // grouped sidebar; every child renders fullBleed because the shell owns the
    // padding. The nav model lives in components/main/settings/settingsNav.ts.
    {
        path: RoutePaths.settings.root(),
        component: SettingsLayout,
        authorize: true,
        // Maintainers see the shell with only their directory pages in it.
        roles: RoleNames.DirectoryEditors,
        fullBleed: true,
        children: [
            // Company
            settingsChild('general', GeneralSettingsPage),
            settingsChild('branding', BrandingSettingsPage),
            settingsChild('access', AccessControlPage),
            settingsChild('billing', BillingPage),
            // Capacity rules
            settingsChild('capacity/working-hours', WorkingHoursSettingsPage),
            settingsChild('capacity/rag-thresholds', RagThresholdsSettingsPage),
            settingsChild('capacity/utilisation', UtilisationSettingsPage),
            settingsChild(
                'capacity/non-working-days',
                NonWorkingDaysSettingsPage
            ),
            // Directory
            settingsChild(
                'directory/roles',
                RolesPage,
                RoleNames.DirectoryEditors
            ),
            settingsChild(
                'directory/roles/:id',
                RoleDetailPage,
                RoleNames.DirectoryEditors
            ),
            settingsChild(
                'directory/tags',
                TagManagementPage,
                RoleNames.DirectoryEditors
            ),
            settingsChild('directory/sync', DirectorySyncPage),
            settingsChild('directory/sync/groups', DirectoryGroupsPage),
            settingsChild('directory/sync/runs/:runId', DirectorySyncRunPage),
            // Integrations
            settingsChild('integrations', IntegrationsOverviewPage),
            settingsChild('integrations/imports', ImportsPage),
            settingsChild('integrations/imports/new', ImportWizardPage),
            settingsChild('integrations/imports/package', ImportPackagePage),
            settingsChild('integrations/imports/runs/:runId', ImportRunPage),
            settingsChild('integrations/sources', SourcesPage),
            settingsChild('integrations/clients', ClientsPage),
            settingsChild(
                'integrations/clients/:id',
                IntegrationClientDetailPage
            ),
            // AI & MCP
            settingsChild('ai/assistants', AssistantsSettingsPage),
            settingsChild('ai/usage', AiUsageSettingsPage),
            settingsChild('ai/provider-keys', ProviderKeysSettingsPage),
            settingsChild('ai/mcp-servers', McpServersTab),
            settingsChild('ai/connections', ServiceConnectionsTab),
            settingsChild('ai/mcp-access', McpAccessSettingsPage),
            settingsChild('ai/mcp-clients', McpClientsSettingsPage),
            settingsChild('ai/mcp-connections', McpGrantsSettingsPage),
            settingsChild('ai/mcp-info', McpTechnicalInfoTab),
            // Audit
            settingsChild('activity', ActivityPage),
            // Old flat tab paths (pre-2026-09 redesign) keep working.
            settingsRedirect('access-control', RoutePaths.settings.access()),
            settingsRedirect(
                'ai-providers',
                RoutePaths.settings.ai.assistants()
            ),
            settingsRedirect('ai-mcp', RoutePaths.settings.ai.mcpServers()),
            settingsRedirect(
                'ai-connections',
                RoutePaths.settings.ai.connections()
            ),
            settingsRedirect('mcp-access', RoutePaths.settings.ai.mcpAccess()),
            settingsRedirect('mcp-info', RoutePaths.settings.ai.mcpInfo())
        ]
    },
    // Former top-nav admin pages now live inside Settings.
    {
        path: RoutePaths.settings.legacy.workingHours(),
        redirectTo: RoutePaths.settings.capacity.workingHours(),
        authorize: true
    },
    {
        path: RoutePaths.settings.legacy.roles(),
        redirectTo: RoutePaths.settings.directory.roles(),
        authorize: true
    },
    {
        path: RoutePaths.settings.legacy.roleDetail(),
        redirectTo: RoutePaths.settings.directory.roleDetail(':id'),
        authorize: true
    },
    {
        path: RoutePaths.settings.legacy.tags(),
        redirectTo: RoutePaths.settings.directory.tags(),
        authorize: true
    },
    // ------------------------------------------------------------ Admin console
    // Platform administration for sysadmins: the same shell with its own tree
    // (components/sysadmin/sysadminNav.ts). Paths are unchanged.
    {
        path: RoutePaths.sysadmin.root(),
        component: SysAdminLayout,
        authorize: true,
        roles: [RoleNames.SysAdmin],
        fullBleed: true,
        children: [
            sysadminChild('customers', CustomersPage),
            sysadminChild('customers/:customerId', CustomerEditPage),
            sysadminChild('users', UsersPage),
            sysadminChild('users/:userId', UserEditPage),
            sysadminChild('data', DataManagementPage),
            sysadminChild('billing', BillingSettingsPage),
            sysadminChild('secret-keys', SecretKeysPage),
            sysadminChild('paddle-webhooks', PaddleWebhookLogPage),
            sysadminChild('ai/assistants', AssistantsPage),
            sysadminChild('ai/assistants/:id', AssistantEditPage),
            sysadminChild('ai/assistants/:keyOrId/test', AssistantTestPage),
            sysadminChild('ai/skills', SkillsPage),
            sysadminChild('ai/providers', AiConnectionsPage),
            sysadminChild('ai/settings', AiPlatformSettingsPage),
            sysadminChild('ai/usage', AiUsagePage),
            sysadminChild('ai/providers/:id', AiConnectionEditPage),
            sysadminChild(
                'ai/providers/:providerId/models/:modelId',
                AiProviderModelEditPage
            ),
            {
                ...sysadminChild('documents', EditDocumentsPage)
            },
            {
                ...sysadminChild('documents/edit/:id', EditDocumentPage)
            },
            {
                ...sysadminChild('contents', ContentManagementPage),
                children: [
                    sysadminChild('', ContentFileListPage),
                    sysadminChild('upload', ContentFileUploadPage),
                    sysadminChild('preview/:filePath', ContentFilePreviewPage)
                ]
            },
            sysadminChild('debug', DebugPage),
            sysadminChild('theme', ThemePage)
        ]
    }
];

export default pages;
